import {
  IntranetActivityKind,
  type IntranetActivityKind as ActivityKind,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIntranetActivity } from '@/products/intranet/features/intranet/use-intranet-activity';

const KIND_LABEL: Record<ActivityKind, string> = {
  [IntranetActivityKind.LIKE]: 'Like',
  [IntranetActivityKind.COMMENT]: 'Kommentar',
  [IntranetActivityKind.REPLY]: 'Antwort',
  [IntranetActivityKind.MENTION]: 'Erwähnung',
};

/** Own intranet counts and the latest things other people did. No ranking. */
export function IntranetActivityCard() {
  const activity = useIntranetActivity(true);
  const data = activity.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Intranet</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {activity.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Aktivität wird geladen …
          </p>
        ) : activity.isError || !data ? (
          <p role="alert" className="text-sm text-destructive">
            Die Intranet-Aktivität konnte nicht geladen werden.
          </p>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Likes erhalten" value={data.likesReceived} />
              <Stat label="Kommentare geschrieben" value={data.commentsWritten} />
              <Stat label="Kommentare erhalten" value={data.commentsReceived} />
              <Stat label="Erwähnungen" value={data.mentions} />
            </dl>
            {data.events.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Aktivität von anderen.</p>
            ) : (
              <ul className="space-y-1">
                {data.events.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.href}
                      className="flex min-h-11 items-center justify-between gap-3 rounded-md px-2 text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
                    >
                      <span className="truncate font-medium">{item.actorName}</span>
                      <span className="shrink-0 text-muted-foreground">
                        {KIND_LABEL[item.kind]}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border px-3 py-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
